import { useMemo } from 'react';

import {
  ViewProps,
  FillInBlanksSentence as FillInBlanksSentenceType,
  FillInBlanksAnswer,
} from './fill-in-blanks.constants';
import SubActivityPrint from '../sub-activity/sub-activity-print';
import { sortFillInBlanksSentences } from './logic/sort-fill-in-blanks-sentences';
import { mergeFillInBlanksBlocks } from './logic/merge-fill-in-blanks-blocks';
import { getFillInBlanksAnswerColour } from './logic/get-fill-in-blanks-answer-colour';
import FillInBlanksTextBlock from './fill-in-blanks-text-block';
import * as Styled from './fill-in-blanks-print.style';

const AnswerBlock = ({
  answerBlock,
  fontSize,
  fontFamily,
}: {
  answerBlock: FillInBlanksAnswer;
  fontSize: string | null;
  fontFamily: string | null;
}) => {
  const colours = useMemo(
    () => getFillInBlanksAnswerColour(answerBlock),
    [answerBlock]
  );

  const shouldShowLabel = useMemo(
    () => !!(answerBlock.showLabel && answerBlock.label),
    [answerBlock]
  );

  return (
    <Styled.FillInBlanksPrintField
      $colours={colours}
      $minWidth={answerBlock.width || 100}
      $punctuation={answerBlock.punctuation}
      $hasLabel={shouldShowLabel}
      $fontSize={fontSize}
      $fontFamily={fontFamily}
    >
      <Styled.FillInBlanksPrintAnswer
        $colours={colours}
        $display="inline-block"
        $isFocused={false}
        $minWidth={answerBlock.width || 100}
        $label={shouldShowLabel ? answerBlock.label : null}
        $fontSize={fontSize}
        $fontFamily={fontFamily}
      >
        <Styled.FillInBlanksPrintTipTapWrapper
          $placeholder={
            answerBlock.showPlaceholderAsScaffold && answerBlock.placeholder
              ? answerBlock.placeholder
              : null
          }
          $isScaffold={answerBlock.showPlaceholderAsScaffold}
          $fontSize={fontSize}
          $fontFamily={fontFamily}
        >
          <Styled.FillInBlanksPrintTipTap
            className="tiptap"
            $fontSize={fontSize}
            $fontFamily={fontFamily}
          >
            <p>
              <br className="ProseMirror-trailingBreak" />
            </p>
          </Styled.FillInBlanksPrintTipTap>
        </Styled.FillInBlanksPrintTipTapWrapper>
      </Styled.FillInBlanksPrintAnswer>
    </Styled.FillInBlanksPrintField>
  );
};

const Sentence = ({
  sentence,
  fontSize,
  fontFamily,
}: {
  sentence: FillInBlanksSentenceType;
  fontSize: string | null;
  fontFamily: string | null;
}) => {
  const mergedBlocks = useMemo(
    () => mergeFillInBlanksBlocks(sentence),
    [sentence]
  );
  return mergedBlocks.map(block => {
    return 'text' in block ? (
      <FillInBlanksTextBlock key={block.itemId} textBlock={block} />
    ) : (
      <AnswerBlock
        key={`${block.itemId}`}
        answerBlock={block}
        fontSize={fontSize}
        fontFamily={fontFamily}
      />
    );
  });
};

const FillInBlanksPrint = ({
  properties,
  subActivityProperties,
}: ViewProps) => {
  const sortedSentences = useMemo(
    () => sortFillInBlanksSentences(properties.fillInBlanks.sentences),
    [properties.fillInBlanks.sentences]
  );

  return (
    <SubActivityPrint subActivityDetails={subActivityProperties}>
      <Styled.FillInBlanksPrint
        $fontFamily={properties.fillInBlanks.fontFamily}
        $fontSize={properties.fillInBlanks.fontSize}
      >
        {sortedSentences.map(sentence => (
          <Styled.FillInBlanksPrintSentence key={sentence.itemId}>
            <Sentence
              sentence={sentence}
              fontSize={properties.fillInBlanks.fontSize}
              fontFamily={properties.fillInBlanks.fontFamily}
            />
          </Styled.FillInBlanksPrintSentence>
        ))}
      </Styled.FillInBlanksPrint>
    </SubActivityPrint>
  );
};

export default FillInBlanksPrint;
